import { Flex, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";
import Lottie from "lottie-react";

import Remixing from "../../../../../../../animations/remixing.json";

import { TMoment } from "MVVM/Views/MatchHistory/@types";
import { shortenMomentTitle } from "MVVM/Views/MatchHistory/utils";
import { ClipThumbnail } from "../ClipThumbnail";

// Moments from this match that already have a rendered clip. They are pulled
// out of the moments list below so each play only appears in one place.
const Highlights = ({
  moments,
  title,
}: {
  moments: TMoment[];
  title: string;
}) => {
  if (!moments.length) return null;

  return (
    <Section>
      <Flex alignItems="center" style={{ gap: 8 }}>
        <Text variant="text.title2" fontWeight="500">
          {title}
        </Text>
        <Count>{moments.length}</Count>
      </Flex>

      <Scroller>
        {moments.map((moment) => {
          // The clip's own title is what the user named it; only fall back to
          // the moment title (and its weapon shortening) when it has none.
          const label = moment.clip?.title || shortenMomentTitle(moment.title);
          const pending = moment.clip?.status === "PENDING";
          return (
            <Card key={moment.id}>
              {pending ? (
                <PendingMedia title="This clip is still rendering">
                  <Lottie
                    animationData={Remixing}
                    loop
                    style={{ height: 44, width: 44 }}
                  />
                  <PendingText>Rendering</PendingText>
                </PendingMedia>
              ) : (
                <CardMedia clip={moment.clip} />
              )}
              <CardTitle title={label}>{label}</CardTitle>
              {pending && <PendingNote>Clip pending</PendingNote>}
            </Card>
          );
        })}
      </Scroller>
    </Section>
  );
};

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const Count = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 700;
  padding: 1px 8px;
`;

// Grid, not a sideways scroller: highlights should all be visible at once
// rather than hidden off the right edge.
const Scroller = styled(Flex)`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
`;

const Card = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

// Overrides ClipThumbnail's fixed 64x36 so the card reads as a video.
const CardMedia = styled(ClipThumbnail)`
  aspect-ratio: 16 / 9;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  height: auto;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }

  svg {
    height: 36px;
    width: 36px;
  }
`;

// Matches ProcessingButton's envy treatment, so a rendering clip reads the same
// here as it does in the moments list.
const transitPulse = keyframes`
  0%, 100% { border-color: rgba(77, 189, 148, 0.35); }
  50% { border-color: rgba(77, 189, 148, 1); }
`;

const PendingText = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const PendingNote = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 500;
`;

const PendingMedia = styled(Flex)`
  align-items: center;
  animation: ${transitPulse} 1.8s ease-in-out infinite;
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.envyAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 10px;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  width: 100%;
`;

const CardTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export { Highlights };
